import { useState } from 'react'
import { ReactComponent as InfoIcon } from 'src/assets/svg/info-empty-circled.svg'
import { ReactComponent as ArrowRight } from 'src/assets/svg/carousel-arrow-right.svg'
import { CuiButton, CuiLogo } from 'front-lib'
import { ResponsiveModalOrBottomSheet } from '../../../components/ResponsiveModalOrBottomSheet/ResponsiveModalOrBottomSheet'
import { IntroType, SuspiciousAbuseStatusResponse } from '../types'
import moment from 'moment'

import './AbuseIntro.scss'
import tracking from '../../../shared/tracking'

type Props = {
  data: SuspiciousAbuseStatusResponse
  onConfirm: () => void
}

const Alert = ({
  variant,
  children
}: {
  variant: 'info' | 'error'
  children: React.ReactNode
}) => {
  return <div className={`abuse-user-page__alert ${variant}`}>{children}</div>
}

const TITLES = {
  [IntroType.order]: 'Action Required',
  [IntroType.cutoff]: 'Your account is locked'
}

const BODY = {
  [IntroType.order]: (
    <>
      <p>To continue, please choose one of the following options:</p>
      <ul>
        <li>
          <b>Confirm your order</b> with the updated pricing
        </li>
        <li>
          <b>Reach out to us</b> if you believe this was a mistake
        </li>
      </ul>
    </>
  ),
  [IntroType.cutoff]: (
    <Alert variant="info">
      <p className="abuse-user-page__alert-title">Order deadline missed</p>
      <p>
        {`Since your order deadline has passed, it's no longer possible to confirm this order with the adjusted pricing. For help unlocking your account, please reach out to our support team.`}
      </p>
    </Alert>
  )
}

const AbuseIntro = ({ data, onConfirm }: Props) => {
  const [isCXModalOpen, setIsCXModalOpen] = useState<boolean>(false)
  const type = moment(data.payload.cutOffDate).isBefore(moment())
    ? IntroType.cutoff
    : IntroType.order

  const onContactSupport = () => {
    tracking.track('Discount Abuse Contact Support Clicked', {
      page_name: 'Discount Abuse'
    })
    setIsCXModalOpen(true)
  }

  const onConfirmOrder = () => {
    // This may be confusing, keep this commented out for now
    // tracking.track('Discount Abuse Confirm Order Clicked', {
    //   page_name: 'Discount Abuse'
    // })
    onConfirm()
  }

  return (
    <>
      <main className="abuse-user-page">
        <div className="abuse-user-page__container">
          <header className="abuse-user-page__header">
            <CuiLogo />
          </header>
          <div className="abuse-user-page__content">
            <h1>
              <InfoIcon width={20} height={20} />
              {TITLES[type]}
            </h1>
            {type === IntroType.order && (
              <h2>We need to verify your discount eligibility.</h2>
            )}
            <p>
              {`Our system flagged that this may not be your first CookUnity account. Since our new subscriber discounts are reserved for first-time customers, we’ve adjusted your pricing to reflect a revised discount.`}
            </p>
          </div>
          <section className="abuse-user-page__next-steps">
            {BODY[type]}
          </section>
          <div className="abuse-user-page__button-container">
            {type === IntroType.order && (
              <CuiButton size="large" onClick={onConfirmOrder}>
                Confirm Order with new discount
                <ArrowRight width={12} height={12} />
              </CuiButton>
            )}
            <CuiButton size="large" fill="outline" onClick={onContactSupport}>
              <InfoIcon width={16} height={16} />
              Contact Support
            </CuiButton>
          </div>
        </div>
      </main>
      <CXModal isOpen={isCXModalOpen} onClose={() => setIsCXModalOpen(false)} />
    </>
  )
}

const CXModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  return (
    <ResponsiveModalOrBottomSheet
      onClose={onClose}
      isOpen={isOpen}
      className="abuse-user-page__cx-modal">
      <div className="abuse-user-page__cx-modal">
        <header>
          <h1>Need Help?</h1>
        </header>
        <main>
          <p>
            We understand this might be an unexpected interruption—and we’re
            sorry for any inconvenience it’s caused. Our system flagged your
            order due to a possible issue with discount eligibility.
          </p>
          <p>
            To help us review your case quickly, please email{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="mailto:support@cookunity.com?subject=Account Review">
              support@cookunity.com
            </a>{' '}
            with the subject line <b>"Account Review"</b>.
          </p>
          <p>
            Be sure to include <b>the email address and phone number</b> linked
            to your account so our team can take a closer look and follow up
            promptly.
          </p>
          <p>Thank you!</p>
          <small className="disclaimer">
            {/* Please include any relevant information about the order, such as
            order number, date, and any other details that may help us identify
            the issue. */}
          </small>
        </main>
        <CuiButton size="large" onClick={onClose}>
          Got it
        </CuiButton>
      </div>
    </ResponsiveModalOrBottomSheet>
  )
}

export default AbuseIntro
